import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  FolderKanban, Loader2, ChevronRight, HandshakeIcon, CheckCircle, CreditCard, ArrowLeft, User
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_LABEL = {
  drafting: 'AI Drafting',
  proposal_ready: 'Proposal Ready',
  accepted: 'Notifying Experts',
  interest_open: 'Awaiting Interest',
  admin_selecting: 'Admin Reviewing',
  expert_selected: 'Payment Due',
  payment_received: 'Payment Received',
  in_progress: 'In Progress',
  completed: 'Completed',
};

export default function AdminProjects({ token }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [interests, setInterests] = useState([]);
  const [notifiedExperts, setNotifiedExperts] = useState([]);
  const [showNotified, setShowNotified] = useState(false);
  const [loadingInterests, setLoadingInterests] = useState(false);
  const [markPaidAmount, setMarkPaidAmount] = useState('');
  const [markingPaid, setMarkingPaid] = useState(false);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/projects/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setProjects(data.projects || []);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) fetchProjects(); /* eslint-disable-line */ }, [token]);

  const openDetail = async (p) => {
    setSelected(p);
    setLoadingInterests(true);
    setNotifiedExperts([]);
    setShowNotified(false);
    try {
      const [ir, nr] = await Promise.all([
        fetch(`${API}/projects/admin/${p.id}/interests`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/projects/admin/${p.id}/notified-experts`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      if (ir.ok) setInterests((await ir.json()).interests || []);
      if (nr.ok) setNotifiedExperts((await nr.json()).experts || []);
    } finally {
      setLoadingInterests(false);
    }
  };

  const markPaid = async () => {
    if (!markPaidAmount || Number(markPaidAmount) < 1) { toast.error('Enter amount in INR'); return; }
    if (!window.confirm(`Manually mark this project as PAID for ₹${Number(markPaidAmount).toLocaleString('en-IN')}? Use this only for out-of-band / test payments.`)) return;
    setMarkingPaid(true);
    try {
      const res = await fetch(`${API}/projects/admin/${selected.id}/mark-paid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ amount_inr: Number(markPaidAmount), note: 'Manually marked paid by admin' })
      });
      if (res.ok) {
        toast.success('Marked as paid. You can now approve the payout.');
        setMarkPaidAmount('');
        // Refresh the detail view + list
        const refresh = await fetch(`${API}/projects/admin/all`, { headers: { Authorization: `Bearer ${token}` } });
        if (refresh.ok) {
          const data = await refresh.json();
          setProjects(data.projects || []);
          const updated = (data.projects || []).find((x) => x.id === selected.id);
          if (updated) setSelected(updated);
        }
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed');
      }
    } finally { setMarkingPaid(false); }
  };

  const selectExpert = async (expertId) => {
    if (!window.confirm('Confirm this expert for the project? All other interested experts will be marked as not selected.')) return;
    const res = await fetch(`${API}/projects/admin/${selected.id}/select-expert`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ expert_id: expertId })
    });
    if (res.ok) {
      toast.success('Expert selected. Industry has been notified.');
      setSelected(null);
      fetchProjects();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || 'Failed');
    }
  };

  const approvePayout = async () => {
    if (!window.confirm('Approve expert payout (80% of received payment; 20% retained as platform fee)?')) return;
    const res = await fetch(`${API}/projects/admin/${selected.id}/approve-payout`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` }
    });
    if (res.ok) {
      const data = await res.json();
      toast.success(`Payout approved. Expert share: ₹${(data.expert_share_paise/100).toLocaleString('en-IN')}, Platform fee: ₹${(data.platform_fee_paise/100).toLocaleString('en-IN')}`);
      setSelected(null);
      fetchProjects();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || 'Failed');
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-white">Industry Projects</h3>
        <p className="text-sm text-zinc-500">Review interests, select experts, and approve payouts.</p>
      </div>

      {projects.length === 0 ? (
        <div className="text-center py-16 text-zinc-500">
          <FolderKanban className="w-12 h-12 mx-auto mb-4 opacity-40" />
          <p>No projects yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {projects.map((p) => (
            <Card key={p.id} className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 cursor-pointer" onClick={() => openDetail(p)} data-testid={`admin-project-${p.id}`}>
              <CardContent className="p-5 flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h4 className="font-semibold text-white truncate">{p.title}</h4>
                    <Badge className="bg-zinc-800 text-zinc-300">{STATUS_LABEL[p.status] || p.status}</Badge>
                    {p.is_referral && <Badge className="bg-blue-500/10 text-blue-400" data-testid={`referral-badge-${p.id}`}>Referral · {p.referral_university_name}</Badge>}
                  </div>
                  <p className="text-xs text-zinc-500 mb-1">Industry: {p.industry_user_email}</p>
                  <p className="text-sm text-zinc-400 line-clamp-2">{p.prompt}</p>
                  <p className="text-xs text-zinc-600 mt-2">{new Date(p.updated_at).toLocaleString()}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-zinc-600 shrink-0 mt-2" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(v) => { if (!v) setSelected(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <span>{selected.title}</span>
                  <Badge>{STATUS_LABEL[selected.status] || selected.status}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="text-sm">
                  <p><b>Industry:</b> {selected.industry_user_email}</p>
                  {selected.is_referral && (
                    <p className="mt-2 text-xs rounded-md border border-blue-500/30 bg-blue-500/5 text-blue-700 px-3 py-2" data-testid="admin-referral-notice">
                      Referral project from <b>{selected.referral_university_name || 'a partner university'}</b> — the university admin selects the innovator.
                    </p>
                  )}
                  <p className="mt-1"><b>Original Prompt:</b> {selected.prompt}</p>
                  {selected.selected_expert_id && (
                    <p className="mt-2 text-green-600"><b>Selected expert:</b> {selected.selected_expert_id}</p>
                  )}
                  {selected.amount_paid_paise > 0 && (
                    <p className="mt-1"><b>Amount Paid:</b> ₹{(selected.amount_paid_paise/100).toLocaleString('en-IN')}</p>
                  )}
                </div>

                {selected.status === 'expert_selected' && (
                  <Card className="border-amber-500/40 bg-amber-500/5">
                    <CardContent className="p-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-amber-600" />
                        <p className="text-sm font-medium">Manual Mark Paid (out-of-band / test)</p>
                      </div>
                      <div className="flex gap-2">
                        <Input type="number" placeholder="Amount in INR" value={markPaidAmount} onChange={(e) => setMarkPaidAmount(e.target.value)} className="flex-1" data-testid="mark-paid-amount" />
                        <Button size="sm" onClick={markPaid} disabled={markingPaid} data-testid="mark-paid-btn">
                          {markingPaid ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Mark Paid'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {selected.status === 'payment_received' && (
                  <Button onClick={approvePayout} className="gap-2" data-testid="approve-payout-btn">
                    <CheckCircle className="w-4 h-4" />
                    Approve Expert Payout (80/20)
                  </Button>
                )}

                {/* Notified experts */}
                <div>
                  <button
                    type="button"
                    onClick={() => setShowNotified((v) => !v)}
                    className="text-sm font-medium flex items-center gap-2 hover:text-primary"
                    data-testid="toggle-notified-experts"
                  >
                    <User className="w-4 h-4" />
                    Notified Experts ({notifiedExperts.length})
                    <span className="text-xs text-muted-foreground">({showNotified ? 'hide' : 'show'})</span>
                  </button>
                  {showNotified && (
                    <div className="mt-2 space-y-2">
                      {notifiedExperts.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No experts notified yet.</p>
                      ) : notifiedExperts.map((e) => (
                        <div key={e.id} className="flex items-center gap-3 border rounded p-2 text-sm">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium">{e.name}</p>
                            <p className="text-xs text-muted-foreground">{e.email}</p>
                            {e.employment && <p className="text-xs text-muted-foreground truncate">{e.employment}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <HandshakeIcon className="w-4 h-4" />
                    Interested Experts ({interests.length})
                  </h4>
                  {loadingInterests ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : interests.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No expressions of interest yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {interests.map((i) => (
                        <Card key={i.id} className={i.status === 'selected' ? 'border-green-500/40 bg-green-500/5' : ''}>
                          <CardContent className="p-3 flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <p className="font-medium">{i.expert_name}</p>
                                <Badge variant="secondary">{i.match_score}% match</Badge>
                                {i.status === 'selected' && <Badge className="bg-green-500/10 text-green-600">Selected</Badge>}
                                {i.status === 'rejected' && <Badge className="bg-zinc-500/10 text-zinc-600">Not selected</Badge>}
                              </div>
                              <p className="text-xs text-muted-foreground">{i.expert_email}</p>
                              <p className="text-xs text-muted-foreground mt-1">{i.employment}</p>
                              {i.notes && <p className="text-sm mt-2 italic text-muted-foreground">"{i.notes}"</p>}
                            </div>
                            {!selected.selected_expert_id && !selected.is_referral && (
                              <Button size="sm" onClick={() => selectExpert(i.expert_id)} data-testid={`select-expert-${i.expert_id}`}>
                                Select
                              </Button>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
